import React, { useEffect, useRef, useState } from 'react';
import Wrapper from './styles';
import { Filters, ProductList, Sort } from '../../components';
import { useLanguageContext } from '../../context/language_context';
import SEOHead from '../../components/SEOHead/';
import { ProductListJsonLd } from '../../components/SEOHead/JsonLd';
import { PAGE_SEO } from '../../utils/seo';
import { useFilterContext } from '../../context/filter_context';
import { useProductsContext } from '../../context/products_context';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, X, Truck } from 'lucide-react';
import { FaCheck } from 'react-icons/fa';
import { getUniqueValues, formatPrice } from '../../utils/helpers';

const ProductsPage = () => {
  const { t, isArabic } = useLanguageContext();
  const {
    setCategoryFilter,
    setTextFilter,
    filters,
    updateFilters,
    clearFilters,
    all_products
  } = useFilterContext();
  const { products } = useProductsContext();
  const location = useLocation();
  const lastCategoryRef = useRef(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Get unique values for filters
  const categories = getUniqueValues(all_products, 'category');
  const colors = getUniqueValues(all_products, 'colors');

  // Category icons mapping
  const categoryIcons = {
    'all': '🏠',
    'kitchen': '🍳',
    'living room': '🛋️',
    'bedroom': '🛏️',
    'bathroom': '🛁',
    'storage': '📦',
  };

  const getCategoryIcon = (cat) => {
    const key = cat.toLowerCase();
    return categoryIcons[key] || '📦';
  };

  useEffect(() => {
    document.title = 'Darista | Products';
  }, []);

  useEffect(() => {
    // Get category and search from URL query params
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category') || 'all';
    const searchParam = params.get('search') || '';

    // Only update if category actually changed
    if (lastCategoryRef.current !== categoryParam) {
      lastCategoryRef.current = categoryParam;
      setCategoryFilter(categoryParam);
    }

    // Set search text from URL
    if (searchParam) {
      setTextFilter(searchParam);
    }
  }, [location.search, setCategoryFilter, setTextFilter]);

  // Handle category click from pills
  const handleCategoryPillClick = (cat) => {
    setCategoryFilter(cat);
  };

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.category && filters.category !== 'all') count++;
    if (filters.color && filters.color !== 'all') count++;
    if (filters.ShopName && filters.ShopName !== 'all') count++;
    if (filters.shipping) count++;
    if (filters.price < filters.max_price) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const seo = isArabic ? PAGE_SEO.products.ar : PAGE_SEO.products.en;

  return (
    <main style={{ direction: isArabic ? 'rtl' : 'ltr' }}>
      <SEOHead
        title={seo.title}
        description={seo.description}
        path='/products'
        lang={isArabic ? 'ar' : 'en'}
      />
      <ProductListJsonLd products={all_products} listName='All Products' />
      <Wrapper>
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <nav className="breadcrumb">
              <Link to="/">{t.nav.home}</Link>
              <ChevronRight />
              <span>{t.nav.products}</span>
            </nav>
            <h1>{t.products.allProducts || 'Our Collection'}</h1>
            <p className="subtitle">
              {isArabic
                ? 'اكتشف منتجاتنا المختارة بعناية لتزيين منزلك'
                : 'Discover our carefully selected products to beautify your home'}
            </p>
          </div>
        </div>

        {/* Mobile Category Pills */}
        <div className="mobile-categories">
          <div className="categories-scroll">
            <div className="categories-pills">
              {categories.map((cat, index) => (
                <button
                  key={index}
                  type="button"
                  className={`category-pill ${filters.category === cat || (cat === 'all' && (!filters.category || filters.category === 'all')) ? 'active' : ''}`}
                  onClick={() => handleCategoryPillClick(cat)}
                >
                  <span className="pill-icon">{getCategoryIcon(cat)}</span>
                  <span>{cat === 'all' ? (t.products?.all || 'All') : cat}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="products-container">
          <div className="products">
            <div className="filters-section">
              <div className="filters-sticky">
                <Filters />
              </div>
            </div>
            <div className="products-section">
              <Sort
                onMobileFilterClick={() => setShowMobileFilters(true)}
                activeFilterCount={activeFilterCount}
              />
              <ProductList />
            </div>
          </div>
        </div>

        {/* Mobile Filter Bottom Sheet */}
        <div
          className={`filter-overlay ${showMobileFilters ? 'active' : ''}`}
          onClick={() => setShowMobileFilters(false)}
        />
        <div className={`filter-sheet ${showMobileFilters ? 'active' : ''}`}>
          <div className="sheet-handle">
            <span></span>
          </div>
          <div className="sheet-header">
            <h3>{t.products?.filters || 'Filters'}</h3>
            <button
              className="sheet-close"
              onClick={() => setShowMobileFilters(false)}
            >
              <X size={16} />
            </button>
          </div>
          <div className="sheet-content">
            {/* Categories Grid */}
            <div className="sheet-section">
              <h4>{t.products?.category || 'Categories'}</h4>
              <div className="sheet-categories">
                {categories.map((cat, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`sheet-category ${filters.category === cat || (cat === 'all' && (!filters.category || filters.category === 'all')) ? 'active' : ''}`}
                    onClick={() => handleCategoryPillClick(cat)}
                  >
                    <span className="cat-icon">{getCategoryIcon(cat)}</span>
                    <span className="cat-name">{cat === 'all' ? (t.products?.all || 'All') : cat}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div className="sheet-section">
              <h4>{t.products?.colors || 'Colors'}</h4>
              <div className="sheet-colors">
                {colors.map((item, index) => {
                  if (item === 'all') {
                    return (
                      <button
                        key={index}
                        name="color"
                        data-color="all"
                        className={`sheet-color ${filters.color === 'all' ? 'active' : ''}`}
                        onClick={updateFilters}
                        style={{ background: '#f3f4f6', fontSize: '0.75rem', fontWeight: 500 }}
                      >
                        {t.products?.all || 'All'}
                      </button>
                    );
                  }
                  return (
                    <button
                      key={index}
                      name="color"
                      data-color={item}
                      className={`sheet-color ${(item === '#FFFFFF' || item === '#ffffff' || (typeof item === 'string' && item.toLowerCase() === 'white')) ? 'white' : ''} ${filters.color === item ? 'active' : ''}`}
                      style={{ background: item }}
                      onClick={updateFilters}
                    >
                      {filters.color === item && <FaCheck size={14} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Price Range */}
            <div className="sheet-section">
              <h4>{t.products?.price || 'Prix'}</h4>
              <div className="sheet-price">
                <input
                  type="range"
                  name="price"
                  id="mobile-price"
                  min={filters.min_price}
                  max={filters.max_price}
                  value={filters.price}
                  onChange={updateFilters}
                  className="price-slider"
                />
                <div className="price-labels">
                  <span className="price-label">{formatPrice(filters.min_price)}</span>
                  <span className="price-label">{formatPrice(filters.price)}</span>
                </div>
              </div>
            </div>

            {/* Free Shipping */}
            <div className="sheet-section">
              <div className="sheet-shipping" onClick={() => {
                const event = { target: { name: 'shipping', type: 'checkbox', checked: !filters.shipping } };
                updateFilters(event);
              }}>
                <input
                  type="checkbox"
                  name="shipping"
                  checked={filters.shipping || false}
                  onChange={updateFilters}
                />
                <label>
                  <Truck size={18} />
                  {t.services?.freeShipping || 'Free Shipping'}
                </label>
              </div>
            </div>
          </div>
          <div className="sheet-footer">
            {activeFilterCount > 0 && (
              <button
                className="sheet-btn outline"
                onClick={() => {
                  clearFilters();
                }}
              >
                {t.products?.clearFilters || 'Clear'} ({activeFilterCount})
              </button>
            )}
            <button
              className={`sheet-btn primary ${activeFilterCount === 0 ? 'full-width' : ''}`}
              onClick={() => setShowMobileFilters(false)}
              style={activeFilterCount === 0 ? { flex: '1 1 100%' } : {}}
            >
              {isArabic ? `عرض ${products.length} منتج` : `Show ${products.length} Products`}
            </button>
          </div>
        </div>
      </Wrapper>
    </main>
  );
};

export default ProductsPage;
